import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, CheckCheck, TrendingUp, TrendingDown, CheckSquare,
  Wrench, AlertTriangle, Building2, Bell,
} from 'lucide-react';
import { cn } from '@utils/cn';
import { useNotificaciones, useMarcarLeida, useMarcarTodasLeidas, useArchivar } from '@hooks/useNotificaciones';
import { useUIStore } from '@/store/ui.store';
import { formatearRelativo } from '@utils/dateFormatters';
import { getUrgencia, URG_COLOR } from '@utils/urgenciaNotificacion';
import type { Notificacion } from '@api/notificaciones';
import { PASO, useTema, type CasaTema } from '@components/M02/casaTema';
import { Button } from '@components/ui/Button';

function getIconConfig(modulo: string, T: CasaTema): { icon: React.ReactNode; bg: string; color: string } {
  const m = modulo?.toLowerCase() ?? '';
  if (m === 'ingresos')   return { icon: <TrendingUp size={14} />,    bg: T.accentSoft, color: T.estados.pagado };
  if (m === 'egresos')    return { icon: <TrendingDown size={14} />,  bg: T.dangerSoft, color: T.danger };
  if (m === 'tareas')     return { icon: <CheckSquare size={14} />,   bg: T.chip,       color: T.estados.cfo };
  if (m === 'postventa')  return { icon: <Wrench size={14} />,        bg: T.accentSoft, color: T.accent };
  if (m === 'bancos')     return { icon: <AlertTriangle size={14} />, bg: T.warnSoft,   color: T.estados.validacion };
  return                         { icon: <Building2 size={14} />,     bg: T.chip,       color: T.estados.cfo };
}

const IconoModulo: React.FC<{ modulo: string }> = ({ modulo }) => {
  const { icon, bg, color } = getIconConfig(modulo, useTema());
  return (
    <div
      className="flex items-center justify-center rounded-full flex-shrink-0"
      style={{ width: 34, height: 34, backgroundColor: bg, color }}
    >
      {icon}
    </div>
  );
};

// ─── Tag badge ────────────────────────────────────────────────
const TagBadge: React.FC<{ label: string }> = ({ label }) => {
  const T = useTema();
  return (
  <span
    className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold flex-shrink-0"
    style={{ backgroundColor: T.chip, color: T.fg2 }}
  >
    {label}
  </span>
  );
};

// ─── Agrupar notificaciones por Hoy / Ayer / Esta semana / Anteriores ──
function agrupar(items: Notificacion[]): { label: string; items: Notificacion[] }[] {
  const ahora = new Date();
  const hoy   = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
  const ayer  = new Date(hoy); ayer.setDate(hoy.getDate() - 1);
  const semana = new Date(hoy); semana.setDate(hoy.getDate() - 7);

  const grupos: Record<string, Notificacion[]> = {
    'Hoy':          [],
    'Ayer':         [],
    'Esta semana':  [],
    'Anteriores':   [],
  };

  for (const n of items) {
    const d = new Date(n.creado_en);
    const dia = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (dia >= hoy)       grupos['Hoy'].push(n);
    else if (dia >= ayer) grupos['Ayer'].push(n);
    else if (dia >= semana) grupos['Esta semana'].push(n);
    else                  grupos['Anteriores'].push(n);
  }

  return Object.entries(grupos)
    .filter(([, arr]) => arr.length > 0)
    .map(([label, items]) => ({ label, items }));
}

// ─── NotificationPanel ────────────────────────────────────────
interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationPanel: React.FC<NotificationPanelProps> = ({ isOpen, onClose }) => {
  const [tab, setTab]       = useState<'todas' | 'noleidas'>('todas');
  const navigate            = useNavigate();
  const T                   = useTema();
  const setNoLeidas         = useUIStore((s) => s.setNoLeidas);

  const { data, isLoading } = useNotificaciones({
    page: 1,
    per_page: 50,
    leida: tab === 'noleidas' ? false : undefined,
    archivada: false,
  });

  const marcarLeida = useMarcarLeida();
  const marcarTodas = useMarcarTodasLeidas();
  const archivar    = useArchivar();

  const items         = data?.data ?? [];
  const noLeidasCount = items.filter(n => !n.leida).length;
  const grupos        = agrupar(items);

  const handleMarcarTodas = () => {
    marcarTodas.mutate(undefined, {
      onSuccess: () => setNoLeidas(0),
    });
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-40 transition-opacity duration-200',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
        style={{ backgroundColor: 'rgba(15,23,42,0.3)' }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 bottom-0 z-50 flex flex-col"
        style={{
          background: T.panelSolid,
          width: 420,
          boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 240ms cubic-bezier(0.4,0,0.2,1)',
          borderLeft: `1px solid ${T.line}`,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${T.lineSoft}` }}>
          <h2 className="text-sm font-bold" style={{ color: T.fg }}>Notificaciones</h2>
          <div className="flex items-center gap-2">
            {noLeidasCount > 0 && (
              <Button
                tema={T}
                variant="secondary"
                size="xs"
                onClick={handleMarcarTodas}
                disabled={marcarTodas.isPending}
                icon={<CheckCheck size={13} />}
              >
                Marcar todas
              </Button>
            )}
            <button
              onClick={onClose}
              className="flex items-center justify-center rounded-lg transition-colors"
              style={{ width: 30, height: 30, color: T.fg3 }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = T.chip;
                (e.currentTarget as HTMLElement).style.color = T.fg;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = '';
                (e.currentTarget as HTMLElement).style.color = T.fg3;
              }}
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-shrink-0 px-5 gap-1 pt-3 pb-2">
          {(['todas', 'noleidas'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={{
                backgroundColor: tab === t ? T.fg : 'transparent',
                color: tab === t ? T.panelSolid : T.fg2,
              }}
              onMouseEnter={(e) => {
                if (tab !== t) (e.currentTarget as HTMLElement).style.backgroundColor = T.panelSoft;
              }}
              onMouseLeave={(e) => {
                if (tab !== t) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
              }}
            >
              {t === 'todas' ? 'Todos' : 'No leídos'}
              {t === 'noleidas' && noLeidasCount > 0 && (
                <span
                  className="flex items-center justify-center rounded-full text-[9px] font-bold"
                  style={{
                    width: 16, height: 16,
                    backgroundColor: tab === 'noleidas' ? T.panelSolid : T.danger,
                    color: tab === 'noleidas' ? T.fg : T.dangerFg,
                  }}
                >
                  {noLeidasCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Lista */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : grupos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 gap-3">
              <Bell size={28} style={{ color: T.fg3 }} />
              <p className="text-sm" style={{ color: T.fg3 }}>Sin notificaciones pendientes</p>
            </div>
          ) : (
            grupos.map((grupo) => (
              <div key={grupo.label}>
                {/* Label de grupo */}
                <div className="px-5 py-2 sticky top-0 z-10"
                  style={{ background: T.panelSolid, borderBottom: `1px solid ${T.lineSoft}` }}>
                  <p className="text-[10px] font-bold uppercase tracking-widest"
                    style={{ color: T.fg3 }}>
                    {grupo.label}
                  </p>
                </div>

                {/* Items */}
                {grupo.items.map((notif) => (
                  <div
                    key={notif.id}
                    className="flex gap-3 px-5 py-3.5 transition-colors cursor-pointer"
                    style={{
                      backgroundColor: notif.leida ? 'transparent' : T.panelSoft,
                      borderBottom: `1px solid ${T.lineSoft}`,
                    }}
                    onClick={() => {
                      if (!notif.leida) marcarLeida.mutate(notif.id);
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = T.chip;
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor =
                        notif.leida ? 'transparent' : T.panelSoft;
                    }}
                  >
                    {/* Ícono + dot */}
                    <div className="flex flex-col items-center gap-1 flex-shrink-0" style={{ paddingTop: 2 }}>
                      <IconoModulo modulo={notif.modulo} />
                      {/* 🔴 El punto DESTACA lo urgente, y deja lo demás como estaba.
                          Ésta es la campana de la barra —lo primero que mira cobranza— y hasta
                          hoy ignoraba la urgencia por completo: la mora y un aviso de rutina se
                          veían idénticos. La pantalla completa sí la usaba, así que la decisión
                          de Mau «la mora en rojo» se cumplía en una cara y en la otra no.

                          ⚠️ Y sólo se pinta lo CRÍTICO y lo URGENTE. Pintar también el `info`
                          con su color habría teñido de VERDE los otros 66 tipos del catálogo
                          —`EGRESO_RECHAZADO`, `ERROR_INTEGRACION`, `SALDO_BAJO_ALERTA`— y el
                          verde se lee como «todo bien». Mau aprobó que la mora saliera en rojo;
                          nadie aprobó pintar de verde una integración caída. Lo neutro se queda
                          neutro. */}
                      {!notif.leida && (
                        <div
                          className="rounded-full"
                          data-urgencia={getUrgencia(notif.tipo)}
                          style={{
                            width: 6, height: 6,
                            backgroundColor: getUrgencia(notif.tipo) === 'info'
                              ? T.estados.cfo                          // el índigo de siempre
                              : URG_COLOR[getUrgencia(notif.tipo)],
                          }} />
                      )}
                    </div>

                    {/* Contenido */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-0.5">
                        <p className="text-xs font-semibold leading-tight" style={{ color: T.fg }}>
                          {notif.tipo.replace(/_/g, ' ')}
                        </p>
                        <TagBadge label={notif.modulo} />
                      </div>

                      <p className="text-xs leading-relaxed mb-1.5" style={{ color: T.fg2 }}>
                        {notif.mensaje}
                      </p>

                      <div className="flex items-center justify-between">
                        <p style={{ color: T.fg3, fontSize: PASO.s10 }}>
                          {formatearRelativo(notif.creado_en)}
                        </p>
                        <button
                          // Cada aviso trae su propio botón "Archivar": en pantalla se
                          // distinguen porque cada uno está pegado a su renglón, pero para
                          // un lector de pantalla —y para cualquier prueba— eran 43 botones
                          // con EXACTAMENTE el mismo nombre. El aria-label le pone apellido.
                          //
                          // 🔴 VA EL ID, NO EL MENSAJE — y no es capricho. El primer intento
                          // fue `Archivar aviso: ${notif.mensaje}`, y el texto libre del aviso
                          // se filtró al nombre accesible: como los mensajes dicen cosas como
                          // "Nuevo lead desde portal broker…", los specs que buscan el botón de
                          // crear con `getByRole('button', {name:/nuevo/i}).first()` empezaron a
                          // agarrar un "Archivar" del panel de avisos —que va antes en el DOM—
                          // en vez del botón de crear. 8 pruebas en rojo. El id
                          // identifica sin arrastrar el vocabulario del mensaje.
                          aria-label={`Archivar aviso #${notif.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            archivar.mutate([notif.id]);
                          }}
                          className="text-[10px] px-2 py-0.5 rounded-md transition-colors"
                          style={{ color: T.fg3, backgroundColor: 'transparent' }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = T.chip;
                            (e.currentTarget as HTMLElement).style.color = T.fg2;
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                            (e.currentTarget as HTMLElement).style.color = T.fg3;
                          }}
                        >
                          Archivar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-3 border-t text-center" style={{ borderColor: T.lineSoft }}>
          <button
            onClick={() => { onClose(); navigate('/notificaciones'); }}
            className="text-xs font-medium"
            style={{ color: T.estados.cfo }}
          >
            Ver todas las notificaciones →
          </button>
        </div>
      </div>
    </>
  );
};

export default NotificationPanel;
